import { useState, useEffect } from 'react';
import './style.css';
import { messengerService } from '../../../functions/messenger';

const MediaViewer = ({ conversationId, token, messages: propMessages, onClose }) => {
  const [activeTab, setActiveTab] = useState('media');
  const [mediaItems, setMediaItems] = useState([]);
  const [linkItems, setLinkItems] = useState([]);
  const [docItems, setDocItems] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(propMessages || []);

  useEffect(() => {
    // Fetch messages if conversationId and token are provided
    const fetchMessages = async () => {
      if (conversationId && token && !propMessages) {
        setLoading(true);
        try {
          // Fetch all messages including deleted ones for media viewing
          const allMessages = await messengerService.getAllMessagesForMedia(conversationId, token);
          setMessages(allMessages);
        } catch (error) {
          console.error('Error fetching messages for media:', error);
        } finally {
          setLoading(false);
        }
      } else if (propMessages) {
        // Use provided messages prop
        setMessages(propMessages);
      }
    };

    fetchMessages();
  }, [conversationId, token, propMessages]);

  useEffect(() => {
    // Process messages to extract media, links, and documents
    const media = [];
    const links = [];
    const docs = [];

    messages.forEach(message => {
      if (message.type === 'image' || message.type === 'video') {
        message.content.media?.forEach(mediaItem => {
          media.push({
            ...mediaItem,
            messageId: message._id,
            timestamp: message.createdAt,
            type: message.type
          });
        });
      } 
      else if (message.type === 'file') {
        message.content.media?.forEach(file => {
          const fileExt = file.name?.split('.').pop().toLowerCase();
          if (['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileExt)) {
            docs.push({
              ...file,
              messageId: message._id,
              timestamp: message.createdAt,
              type: 'document'
            });
          }
        });
      }
      // Extract links from text messages
      else if (message.type === 'text' && message.content.text) {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const text = message.content.text;
        let match;
        
        while ((match = urlRegex.exec(text)) !== null) {
          links.push({
            url: match[0],
            preview: text.substring(match.index - 20 > 0 ? match.index - 20 : 0, match.index) + 
                    '...' + 
                    text.substring(match.index + match[0].length, match.index + match[0].length + 20),
            timestamp: message.createdAt,
            messageId: message._id
          });
        }
      }
    });

    setMediaItems(media);
    setLinkItems(links);
    setDocItems(docs);
  }, [messages]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop().toLowerCase() || '';
    switch(ext) {
      case 'pdf':
        return <span className="notranslate">📄</span>;
      case 'doc':
      case 'docx':
        return <span className="notranslate">📝</span>;
      case 'xls':
      case 'xlsx':
        return <span className="notranslate">📊</span>;
      case 'ppt':
      case 'pptx':
        return <span className="notranslate">📑</span>;
      case 'txt':
        return <span className="notranslate">📄</span>;
      default:
        return <span className="notranslate">📎</span>;
    }
  };

  const handleMediaClick = (item) => {
    setSelectedMedia(item);
  };

  const closeMediaPreview = () => {
    setSelectedMedia(null);
  };

  const renderMediaGrid = () => (
    <div className="media-grid">
      {mediaItems.map((item, index) => (
        <div key={`${item.messageId}-${index}`} className="media-item">
          {item.type === 'image' ? (
            <img 
              src={item.url} 
              alt="Media" 
              className="media-preview"
              onClick={() => handleMediaClick(item)}
            />
          ) : (
            <video 
              className="media-preview"
              onClick={() => handleMediaClick(item)}
            >
              <source src={item.url} type={`video/${item.url.split('.').pop()}`} />
            </video>
          )}
          <div className="media-date">{formatDate(item.timestamp)}</div>
        </div>
      ))}
      {mediaItems.length === 0 && <div className="empty-state">No media shared yet</div>}
      
      {selectedMedia && (
        <div className="media-preview-overlay" onClick={closeMediaPreview}>
          <div className="media-preview-container" onClick={e => e.stopPropagation()}>
            <button className="close-preview-btn" onClick={closeMediaPreview}>×</button>
            {selectedMedia.type === 'image' ? (
              <img 
                src={selectedMedia.url} 
                alt="Full size preview" 
                className="full-media-preview"
              />
            ) : (
              <video 
                className="full-media-preview" 
                controls 
                autoPlay
              >
                <source src={selectedMedia.url} type={`video/${selectedMedia.url.split('.').pop()}`} />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderLinksList = () => (
    <div className="links-list">
      {linkItems.map((link, index) => (
        <div key={`${link.messageId}-${index}`} className="link-item">
          <div className="link-preview">
            <div className="link-icon">🔗</div>
            <div className="link-details">
              <a 
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-url"
              >
                {link.url.length > 40 ? `${link.url.substring(0, 40)}...` : link.url}
              </a>
              <div className="link-snippet">{link.preview}</div>
              <div className="link-date">{formatDate(link.timestamp)}</div>
            </div>
          </div>
        </div>
      ))}
      {linkItems.length === 0 && <div className="empty-state">No links shared yet</div>}
    </div>
  );

  const renderDocsList = () => (
    <div className="docs-list">
      {docItems.map((doc, index) => {
        const isPdf = doc.name?.toLowerCase().endsWith('.pdf');
        return (
          <div key={`${doc.messageId}-${index}`} className="doc-item">
            <div className="doc-icon">{getFileIcon(doc.name)}</div>
            <div className="doc-details">
              <a 
                href={doc.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={isPdf ? 'doc-link' : ''}
              >
                <div className="doc-name">{doc.name}</div>
              </a>
              <div className="doc-meta">
                <span className="doc-size">
                  {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                </span>
                <span className="doc-date">{formatDate(doc.timestamp)}</span>
              </div>
            </div>
            {!isPdf && (
              <a 
                href={doc.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="doc-download"
                download
              >
                ⬇️
              </a>
            )}
          </div>
        );
      })}
      {docItems.length === 0 && <div className="empty-state">No documents shared yet</div>}
    </div>
  );

  return (
    <div className="media-viewer-overlay">
      <div className="media-viewer">
        <div className="media-viewer-header">
          <h3>Media, links, and documents</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="media-tabs">
          <button 
            className={`tab ${activeTab === 'media' ? 'active' : ''}`}
            onClick={() => setActiveTab('media')}
          >
            Media ({mediaItems.length})
          </button>
          <button 
            className={`tab ${activeTab === 'links' ? 'active' : ''}`}
            onClick={() => setActiveTab('links')}
          >
            Links ({linkItems.length})
          </button>
          <button 
            className={`tab ${activeTab === 'docs' ? 'active' : ''}`}
            onClick={() => setActiveTab('docs')}
          >
            Documents ({docItems.length})
          </button>
        </div>
        
        <div className="media-content">
          {loading ? (
            <div className="empty-state">Loading...</div>
          ) : (
            <>
              {activeTab === 'media' && renderMediaGrid()}
              {activeTab === 'links' && renderLinksList()}
              {activeTab === 'docs' && renderDocsList()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaViewer;
